/* @flow */
/**
 * Redux Toolkit slice and async actions for conversation thread report searches, saved searches,
 * exports, and pagination.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import {
  updateDialogStates,
  toggleSpinner, togglePaginationSpinner
} from '../dialogStates/dialogStatesSlice';
import getflattenResponse, { cryptoRand } from '../../helpers/common';
import conversationThreadReportHeader from '../../constants/conversationThreadReportHeader';
import { mapInputFieldsToRequest, mapRequestToInputFields, setValidFields } from '../../helpers/dataHelpers';
import { conversationThreadReportFieldList, initialConversationThreadReportFieldState } from '../../constants/staticData';
import appConfig from '../../appConfig';
import conversationThreadReportFieldProperties from '../../constants/fieldProperties/conversationThreadReportFieldProperties';

export const name = 'conversationThreadReport';

function createInitialState() {
  return {
    conversationThreadReportResultData: {},
    conversationThreadReportSearchId: '',
    conversationThreadReportTotalCount: 0,
    conversationThreadReportNextResults: {},
    conversationThreadReportPrevResults: {},
    page: 0,
    rowsPerPage: 100,

    isSavedSearchModified: false,
    conversationThreadReportRequestBody: null,
    conversationThreadReportInputFieldData: { ...initialConversationThreadReportFieldState },
    conversationThreadReportSelectedFields: [],
    conversationThreadReportBookmarkName: '',
    conversationThreadReportBookmarkId: '',
    requestError: false,
    jumpToPage: 0,
    apiCallAfterAppend: false,
    apiCallBeforeAppend: false
  };
}

export const initialState = createInitialState();

/**
 * Reducer that updates conversation thread report page in conversation thread report state from the
 * action payload.
 */
function updateConversationThreadReportPage(state: Object, action: Object) {
  // UPDATE_CONVERSATION_THREAD_REPORT_PAGE

  return {
    ...state,
    page: action.payload?.page ? action.payload?.page : action.payload
  };
}

/**
 * Reducer that updates conversation thread report rows per page in conversation thread report state
 * from the action payload.
 */
function updateConversationThreadReportRowsPerPage(state: Object, action: Object) {
  // UPDATE_CONVERSATION_THREAD_REPORT_ROWS_PER_PAGE

  return {
    ...state,
    rowsPerPage: action.payload?.rowsPerPage ? action.payload?.rowsPerPage : action.payload
  };
}

/**
 * Reducer that updates conversation thread report request body in conversation thread report state
 * from the action payload.
 */
function updateConversationThreadReportRequestBody(state: Object, action: Object) {
  // UPDATE_CONVERSATION_THREAD_REPORT_REQUEST_BODY
  const requestExist = JSON.stringify(state.conversationThreadReportRequestBody);
  return {
    ...state,
    conversationThreadReportRequestBody: action.payload,
    isSavedSearchModified: Boolean(
      state.conversationThreadReportBookmarkId && requestExist !== JSON.stringify(action.payload)
    )
  };
}

/**
 * Reducer that updates is saved search modified in conversation thread report state from the action
 * payload.
 */
function updateIsSavedSearchModified(state: Object, action: Object) {
  return {
    ...state,
    isSavedSearchModified: Boolean(action.payload)
  };
}

/**
 * Reducer that updates conversation thread report request body on same bookmark in conversation
 * thread report state from the action payload.
 */
function updateConversationThreadReportRequestBodyOnSameBookmark(state: Object, action: Object) {
  // UPDATE_CONVERSATION_THREAD_SEARCH_REQUEST_ON_SAME_BOOKMARK
  return {
    ...state,
    conversationThreadReportRequestBody: action.payload
  };
}

/**
 * Reducer that resets conversation thread report results in conversation thread report state.
 */
function resetConversationThreadReportResults(state: Object) {
  // RESET_CONVERSATION_THREAD_REPORT_RESULTS
  return {
    ...state,
    conversationThreadReportResultData: {},
    conversationThreadReportSearchId: '',
    conversationThreadReportTotalCount: 0,
    conversationThreadReportNextResults: {},
    conversationThreadReportPrevResults: {},
    page: 0,
    rowsPerPage: 100
  };
}

/**
 * Reducer that resets conversation thread report request in conversation thread report state.
 */
function resetConversationThreadReportRequest(state: Object) {
  // RESET_CONVERSATION_THREAD_REPORT_REQUEST
  return {
    ...state,
    isSavedSearchModified: false,
    conversationThreadReportRequestBody: null,
    conversationThreadReportInputFieldData: { ...initialConversationThreadReportFieldState },
    conversationThreadReportSelectedFields: [],
    conversationThreadReportBookmarkName: '',
    conversationThreadReportBookmarkId: '',
    requestError: false
  };
}

/**
 * Reducer that updates conversation thread report fields in conversation thread report state from
 * the action payload.
 */
function updateConversationThreadReportFields(state: Object, action: Object) {
  // UPDATE_CONVERSATION_THREAD_REPORT_SELECTED_FIELDS
  return {
    ...state, conversationThreadReportSelectedFields: action.payload
  };
}

/**
 * Reducer that updates conversation thread report input values in conversation thread report state
 * from the action payload.
 */
function updateConversationThreadReportInputValues(state: Object, action: Object) {
  // UPDATE_CONVERSATION_THREAD_REPORT_INPUT_VALUES
  return { ...state, conversationThreadReportInputFieldData: action.payload };
}

/**
 * Reducer that updates conversation thread report bookmark id in conversation thread report state
 * from the action payload.
 */
function updateConversationThreadReportBookmarkId(state: Object, action: Object) {
  // UPDATE_CONVERSATION_THREAD_REPORT_BOOKMARK_ID
  return {
    ...state,
    isSavedSearchModified: false,
    conversationThreadReportBookmarkId: action.payload
  };
}

/**
 * Reducer that clears saved conversation thread report in conversation thread report state.
 */
function clearSavedConversationThreadReport(state: Object) {
  // CLEAR_CONVERSATION_THREAD_REPORT_BOOKMARK
  return {
    ...state,
    isSavedSearchModified: false,
    conversationThreadReportBookmarkId: '',
    conversationThreadReportBookmarkName: ''
  };
}

/**
 * Reducer that updates conversation thread report bookmark name in conversation thread report state
 * from the action payload.
 */
function updateConversationThreadReportBookmarkName(state: Object, action: Object) {
  // UPDATE_CONVERSATION_THREAD_REPORT_BOOKMARK_NAME
  return {
    ...state,
    isSavedSearchModified: false,
    conversationThreadReportBookmarkName: action.payload
  };
}

/**
 * Reducer that updates conversation thread report results in conversation thread report state from
 * the action payload.
 */
function updateConversationThreadReportResults(state: Object, action: Object) {
  // UPDATE_CONVERSATION_THREAD_REPORT_RESULTS
  const payload = {
    data: getflattenResponse(action.payload.data),
    dataNext: null
  };

  return {
    ...state,
    conversationThreadReportResultData: payload.data,
    conversationThreadReportTotalCount: payload.data.pages.totalResources,
    conversationThreadReportSearchId: cryptoRand().toString().slice(2, 10),
    selectedRecordsCount: 0,
    conversationThreadReportNextResults: null,
    conversationThreadReportPrevResults: null
  };
}

/**
 * Reducer that updates conversation thread report search id in conversation thread report state
 * from the action payload.
 */
function updateConversationThreadReportSearchId(state: Object) {
  // UPDATE_CONVERSATION_THREAD_REPORT_SEARCH_ID
  return { ...state, conversationThreadReportSearchId: cryptoRand().toString().slice(2, 10) };
}

/**
 * Reducer that resets conversation thread report input data in conversation thread report state.
 */
function resetConversationThreadReportInputData(state: Object) {
  // RESET_CONVERSATION_THREAD_REPORT_INPUT_VALUES
  return {
    ...state,
    conversationThreadReportInputFieldData: initialState.conversationThreadReportInputFieldData
  };
}

/**
 * Reducer that updates conversation thread report next result in conversation thread report state
 * from the action payload.
 */
function updateConversationThreadReportNextResult(state: Object, action: Object) {
  // UPDATE_CONVERSATION_THREAD_REPORT_NEXT_RESULTS
  const payload = {
    data: getflattenResponse(action.payload.currentResultSet),
    dataNext: getflattenResponse(action.payload.response.data)
  };

  return {
    ...state,
    conversationThreadReportResultData: payload.data,
    conversationThreadReportNextResults: payload.dataNext,
    conversationThreadReportTotalPages: payload.data.pages.totalPages,
    conversationThreadReportTotalCount: payload.data.pages.totalResources
  };
}

/**
 * Reducer that updates conversation thread report prev result in conversation thread report state
 * from the action payload.
 */
function updateConversationThreadReportPrevResult(state: Object, action: Object) {
  // UPDATE_CONVERSATION_THREAD_REPORT_PREV_RESULTS
  const payload = {
    data: getflattenResponse(action.payload.currentResultSet),
    dataPrev: getflattenResponse(action.payload.response.data)
  };

  return {
    ...state,
    conversationThreadReportResultData: payload.data,
    conversationThreadReportPrevResults: payload.dataPrev,
    conversationThreadReportTotalPages: payload.data.pages.totalPages,
    conversationThreadReportTotalCount: payload.data.pages.totalResources
  };
}

/**
 * Reducer that updates conversation thread report offset in conversation thread report state.
 */
function conversationThreadReportOffset(state: Object, action: Object) {
  // CONVERSATION_THREAD_REPORT_RESULT_OFFSET
  const payload = {
    data: getflattenResponse(action.payload.response.data)
  };

  return {
    ...state,
    conversationThreadReportResultData: payload.data,
    conversationThreadReportTotalCount: payload.data.pages.totalResources,
    conversationThreadReportSearchId: cryptoRand().toString().slice(2, 10),
    selectedRecordsCount: 0,
    conversationThreadReportNextResults: null,
    conversationThreadReportPrevResults: null
  };
}

/**
 * Reducer that promotes the cached next report page and stores the current page as previous cache.
 */
function ChangeReportAppendNextResultSet(state: Object) {
  // UPDATE_CONVERSATION_THREAD_REPORT_NEXT_APPEND
  const updatedCurrentResultSet = state.conversationThreadReportResultData;
  const updatedNextResultSet = state.conversationThreadReportNextResults;

  const payload = {
    dataPrev: updatedCurrentResultSet,
    data: updatedNextResultSet
  };

  return {
    ...state,
    conversationThreadReportResultData: payload.data,
    conversationThreadReportPrevResults: payload.dataPrev,
    conversationThreadReportNextResults: null
  };
}

/**
 * Reducer that promotes the cached next report page and updates jump-to-page prefetch flags.
 */
function ChangeReportAppendJumpToPageNextResultSet(state: Object, action: Object) {
  // UPDATE_CONVERSATION_THREAD_REPORT_NEXT_APPEND
  const updatedCurrentResultSet = state.conversationThreadReportResultData;
  const updatedNextResultSet = state.conversationThreadReportNextResults;

  const payload = {
    dataPrev: updatedCurrentResultSet,
    data: updatedNextResultSet
  };

  // Jump-to-page navigation works in 1000-record API windows based on page size.
  const numberString = state.page.toString();

  const rowPerPage = action.payload;

  let checkApiCallApend = false;
  let checkApiCallPrepend = false;

  if (Number(rowPerPage) === 100) {
    checkApiCallApend = numberString[numberString.length - 1] === '9';
    checkApiCallPrepend = numberString[numberString.length - 1] === '0';
  } else if (Number(rowPerPage) === 250) {
    checkApiCallApend = (Number(numberString) + 1) % 4 === 0;
    checkApiCallPrepend = Number(numberString) % 4 === 0;
  } else if (Number(rowPerPage) === 500) {
    checkApiCallApend = (Number(numberString) + 1) % 2 === 0;
    checkApiCallPrepend = Number(numberString) % 2 === 0;
  }

  return {
    ...state,
    conversationThreadReportResultData: payload.data,
    conversationThreadReportPrevResults: payload.dataPrev,
    conversationThreadReportNextResults: null,
    apiCallAfterAppend: checkApiCallApend,
    apiCallBeforeAppend: checkApiCallPrepend
  };
}

/**
 * Reducer that promotes the cached previous report page and stores the current page as next cache.
 */
function ChangeReportAppendPrevResultSet(state: Object) {
  // UPDATE_CONVERSATION_THREAD_REPORT_PREV_APPEND
  const updatedPrevResultSet = state.conversationThreadReportPrevResults;
  const updatedCurrentResultSet = state.conversationThreadReportResultData;

  const payload = {
    data: updatedPrevResultSet,
    dataNext: updatedCurrentResultSet
  };

  return {
    ...state,
    conversationThreadReportResultData: payload.data,
    conversationThreadReportNextResults: payload.dataNext,
    conversationThreadReportPrevResults: null
  };
}

/**
 * Reducer that promotes the cached previous report page and updates jump-to-page prefetch flags.
 */
function ChangeReportAppendJumpToPagePrevResultSet(state: Object, action: Object) {
  // UPDATE_CONVERSATION_THREAD_REPORT_PREV_APPEND
  const updatedPrevResultSet = state.conversationThreadReportPrevResults;
  const updatedCurrentResultSet = state.conversationThreadReportResultData;

  const payload = {
    data: updatedPrevResultSet,
    dataNext: updatedCurrentResultSet
  };

  // Jump-to-page navigation works in 1000-record API windows based on page size.
  const numberString = state.page.toString();

  const rowPerPage = action.payload;

  let checkApiCallApend = false;
  let checkApiCallPrepend = false;

  if (Number(rowPerPage) === 100) {
    checkApiCallApend = numberString[numberString.length - 1] === '9';
    checkApiCallPrepend = numberString[numberString.length - 1] === '0';
  } else if (Number(rowPerPage) === 250) {
    checkApiCallApend = (Number(numberString) + 1) % 4 === 0;
    checkApiCallPrepend = Number(numberString) % 4 === 0;
  } else if (Number(rowPerPage) === 500) {
    checkApiCallApend = (Number(numberString) + 1) % 2 === 0;
    checkApiCallPrepend = Number(numberString) % 2 === 0;
  }

  return {
    ...state,
    conversationThreadReportResultData: payload.data,
    conversationThreadReportNextResults: payload.dataNext,
    conversationThreadReportPrevResults: null,
    apiCallAfterAppend: checkApiCallApend,
    apiCallBeforeAppend: checkApiCallPrepend
  };
}

/**
 * Reducer that stores meta data data in conversation thread report state.
 */
function getMetaData(state: Object, action: Object) {
  // GET_CONVERSATION_META_DATA
  return {
    ...state,
    conversationMetaData: action.payload.conversationCodes.objects
  };
}

/**
 * Reducer that updates next result setto null in conversation thread report state from the action
 * payload.
 */
function updateNextResultSettoNull(state: Object) {
  return {
    ...state,
    conversationThreadReportNextResults: null
  };
}

/**
 * Reducer that updates previous result setto null in conversation thread report state from the
 * action payload.
 */
function updatePreviousResultSettoNull(state: Object) {
  return {
    ...state,
    conversationThreadReportPrevResults: null
  };
}

/**
 * Reducer that updates jump to page in conversation thread report state from the action payload.
 */
function updateJumpToPage(state: Object, action: Object) {
  return {
    ...state,
    jumpToPage: action.payload,
    triggerJumpToPage: true
  };
}
/**
 * Reducer that updates trigger jump to page in conversation thread report state from the action
 * payload.
 */
function updateTriggerJumpToPage(state: Object, action: Object) {
  return {
    ...state,
    triggerJumpToPage: action.payload
  };
}

/**
 * Reducer that updates API call after append in conversation thread report state from the action
 * payload.
 */
function updateApiCallAfterAppend(state: Object, action: Object) {
  return {
    ...state,
    apiCallAfterAppend: action.payload
  };
}

/**
 * Reducer that updates API call before prepend in conversation thread report state from the action
 * payload.
 */
function updateApiCallBeforePrepend(state: Object, action: Object) {
  return {
    ...state,
    apiCallBeforeAppend: action.payload
  };
}

/**
 * Collects slice reducers for createSlice registration.
 */
function createReducers() {
  return {
    updateConversationThreadReportPage,
    updateConversationThreadReportRowsPerPage,
    updateConversationThreadReportRequestBody,
    updateIsSavedSearchModified,
    updateConversationThreadReportRequestBodyOnSameBookmark,
    resetConversationThreadReportResults,
    resetConversationThreadReportRequest,
    updateConversationThreadReportFields,
    updateConversationThreadReportInputValues,
    updateConversationThreadReportBookmarkId,
    clearSavedConversationThreadReport,
    updateConversationThreadReportBookmarkName,
    updateConversationThreadReportResults,
    updateConversationThreadReportSearchId,
    resetConversationThreadReportInputData,
    updateConversationThreadReportNextResult,
    updateConversationThreadReportPrevResult,
    conversationThreadReportOffset,
    ChangeReportAppendNextResultSet,
    ChangeReportAppendPrevResultSet,
    getMetaData,
    ChangeReportAppendJumpToPageNextResultSet,
    ChangeReportAppendJumpToPagePrevResultSet,
    updateJumpToPage,
    updateTriggerJumpToPage,
    updateApiCallAfterAppend,
    updateApiCallBeforePrepend,
    updatePreviousResultSettoNull,
    updateNextResultSettoNull
  };
}

export const reducers = createReducers();
export const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };

/**
 * Creates a thunk that calls the conversationThreadReportSearch API for the conversation thread
 * report workflow and updates Redux state.
 */
function fetchConversationThreadReports() {
  return createAsyncThunk(
    `${name}/fetchConversationThreadReports`,
    async ({
      data,
      callback,
      modifiedrequestBody = {}
    }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      // Secondary header fields are requested so report rows have complete display data.
      const secondaryFields = [];
      const fields = conversationThreadReportHeader
        .filter((header) => header.default)
        .map((header) => {
          if (header.secondary) {
            secondaryFields.push(header.secondary);
          }
          return header.primary;
        });
      const state = getState();
      const rowsPerPageOption = state.searchpaneldata?.rowPerPageOption;
      let requestData = {};
      if (data) {
        requestData = mapInputFieldsToRequest(data, 'conversationThreadReport');
        const appliedConversationThreadSearchField = new Set(
          requestData.map((field) => field.fieldName)
        );
        dispatch(actions.updateConversationThreadReportInputValues(data));
        dispatch(actions.updateConversationThreadReportFields(conversationThreadReportFieldList
          .map((field) => field.key)
          .filter((field) => appliedConversationThreadSearchField.has(field))));
      } else {
        requestData = state.conversationThreadReport.conversationThreadReportRequestBody.search;
      }

      const requestBody = (state.conversationThreadReport.conversationThreadReportRequestBody)
        ? {
          ...state.conversationThreadReport.conversationThreadReportRequestBody,
          search: requestData,
          offset: '0',
          ...modifiedrequestBody
        } : {
          fields: [...fields, ...secondaryFields],
          search: requestData,
          count: appConfig.SearchThresholdLimit,
          offset: '0',
          ...modifiedrequestBody
        };
      request({
        api: 'conversationThreadReportSearch',
        method: 'post',
        data: requestBody,
        cancellable: true
      }, dispatch, getState)
        .then((response) => {
          dispatch(actions.updateConversationThreadReportRequestBody(requestBody));
          dispatch(toggleSpinner(false));
          callback(response.data);
          dispatch(actions.updateConversationThreadReportRowsPerPage(rowsPerPageOption));
          dispatch(actions.updateConversationThreadReportResults(response));
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          callback(null, error);
        });
    }
  );
}

/**
 * Creates a thunk that calls the conversationThreadReportExportLargeFile API for the conversation
 * thread report workflow and updates Redux state.
 */
function exportConversationThreadReport() {
  return createAsyncThunk(
    `${name}/exportConversationThreadReport`,
    async ({
      callback, filetype
    }, { getState, dispatch }) => {
      const state = getState();
      dispatch(toggleSpinner(true));
      const searchRequestBody = {
        ...state.conversationThreadReport.conversationThreadReportRequestBody
      };
      const conversationThreadReportRequestBody = {
        search: [...(searchRequestBody.search) || []],
        fields: [...(searchRequestBody.fields) || []].filter((field: string) => (
          !appConfig.excludedPOReportFieldsInExportedFile.includes(field)
        )),
        searchType: searchRequestBody.searchType,
        fileFormat: filetype
      };
      request({
        api: 'conversationThreadReportExportLargeFile',
        method: 'post',
        data: conversationThreadReportRequestBody
      }, dispatch, getState)
        .then((response) => {
          callback(response);
          dispatch(toggleSpinner(false));
        })
        .catch((error) => {
          callback(null, error);
          dispatch(toggleSpinner(false));
        });
    }
  );
}

/**
 * Creates a thunk that calls the conversationThreadReportSearch API for the conversation thread
 * report workflow and updates Redux state.
 */
function fetchSavedConversationThreadReportResults() {
  return createAsyncThunk(
    `${name}/fetchSavedConversationThreadReportResults`,
    async ({
      conversationThreadReportRequestData,
      callback
    }, { getState, dispatch }) => {
      const state = getState();
      const rowsPerPageOption = state.searchpaneldata?.rowPerPageOption;
      let requestBody = { ...conversationThreadReportRequestData.searchCriteria, offset: '0' };
      requestBody = setValidFields(conversationThreadReportFieldProperties, requestBody);
      dispatch(toggleSpinner(true));
      dispatch(updateDialogStates({ executeBookmarkSpinner: true }));
      request({
        api: 'conversationThreadReportSearch',
        method: 'post',
        data: {
          savedSearchID: conversationThreadReportRequestData.id,
          ...requestBody
        },
        cancellable: true
      }, dispatch, getState)
        .then((response) => {
          const fields = mapRequestToInputFields([
            ...conversationThreadReportRequestData.searchCriteria.search
          ]);
          const conversationThreadSearchFields = new Set(
            conversationThreadReportRequestData.searchCriteria.search.map(
              (field) => field.fieldName
            )
          );
          dispatch(actions.updateConversationThreadReportInputValues(
            fields
          ));
          dispatch(actions.updateConversationThreadReportRequestBody(requestBody));
          dispatch(actions.updateConversationThreadReportFields(conversationThreadReportFieldList
            .map((field) => field.key)
            .filter((field) => conversationThreadSearchFields.has(field))));
          dispatch(actions.updateConversationThreadReportResults(response));
          dispatch(actions.updateConversationThreadReportSearchId());
          dispatch(actions.updateConversationThreadReportPage(0));
          dispatch(actions.updateConversationThreadReportRowsPerPage(rowsPerPageOption));
          dispatch(
            actions.updateConversationThreadReportBookmarkId(
              conversationThreadReportRequestData.id
            )
          );
          dispatch(
            actions.updateConversationThreadReportBookmarkName(
              conversationThreadReportRequestData.name
            )
          );
          dispatch(toggleSpinner(false));
          dispatch(updateDialogStates({ executeBookmarkSpinner: false }));
          if (callback) {
            callback(response);
          }
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          dispatch(updateDialogStates({ executeBookmarkSpinner: false }));
          console.log(error);
          if (callback) {
            callback(null, error);
          }
        });
    }
  );
}

/**
 * Creates a thunk that calls the conversationThreadReportSearch API for the conversation thread
 * report workflow and updates Redux state.
 */
function fetchConversationThreadreportsResultsOffset() {
  return createAsyncThunk(
    `${name}/fetchConversationThreadreportsResultsOffset`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      if (state.conversationThreadReport.conversationThreadReportRequestBody
        && state.conversationThreadReport.conversationThreadReportResultData) {
        const conversationThreadReportUpdatedRequest = state.conversationThreadReport
          .conversationThreadReportRequestBody;
        delete conversationThreadReportUpdatedRequest.offset;
        conversationThreadReportUpdatedRequest.offset = '0';
        dispatch(toggleSpinner(true));
        request({
          api: 'conversationThreadReportSearch',
          method: 'post',
          data: conversationThreadReportUpdatedRequest
        }, dispatch, getState)
          .then((response) => {
            dispatch(actions.conversationThreadReportOffset(response));
            dispatch(toggleSpinner(false));
            dispatch(actions.updateConversationThreadReportSearchId());
          })
          .catch((error) => {
            console.log('NEXT Error ', error);
            dispatch(toggleSpinner(false));
          });
      }
    }
  );
}

/**
 * Creates a thunk wrapper that dispatches the supplied first-page report reload thunk.
 */
function conversationThreadReportDataOffset() {
  return createAsyncThunk(
    `${name}/conversationThreadReportDataOffset`,
    async ({
      pofetchConversationThreadreportsResultsOffset
    }, { dispatch }) => {
      dispatch(pofetchConversationThreadreportsResultsOffset());
    }
  );
}

/**
 * Creates a thunk that calls the conversationThreadReportSearch API for the conversation thread
 * report workflow and updates Redux state.
 */
function fetchConversationThreadReportJumpToPageNextResultSet() {
  return createAsyncThunk(
    `${name}/fetchConversationThreadReportJumpToPageNextResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      dispatch(actions.updateApiCallAfterAppend(false));
      if (state.conversationThreadReport?.conversationThreadReportRequestBody
        && state.conversationThreadReport?.conversationThreadReportResultData?.pages?.next) {
        const currentResultSet = state.conversationThreadReport
          .conversationThreadReportResultData;
        let updatedRequest = state.conversationThreadReport
          .conversationThreadReportRequestBody;
        const searchResultNextOffset = state.conversationThreadReport
          .conversationThreadReportResultData.pages.next;
        // Cursor values arrive as query fragments; extract only the offset segment.
        const resNext = searchResultNextOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: searchResultNextOffset.slice(7, resNext)
        };
        dispatch(togglePaginationSpinner({ isFetchingNextData: true }));
        request({
          api: 'conversationThreadReportSearch',
          method: 'post',
          data: updatedRequest
        }, dispatch, getState)
          .then((response) => {
            dispatch(
              actions.updateConversationThreadReportRequestBodyOnSameBookmark(updatedRequest)
            );
            dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
            dispatch(toggleSpinner(false));
            dispatch(
              actions.updateConversationThreadReportNextResult({ response, currentResultSet })
            );
          })
          .catch((error) => {
            dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
            console.log('NEXT Error ', error);
          });
      }
    }
  );
}

/**
 * Creates a thunk that calls the conversationThreadReportSearch API for the conversation thread
 * report workflow and updates Redux state.
 */
function fetchConversationThreadReportNextResultSet() {
  return createAsyncThunk(
    `${name}/fetchConversationThreadReportNextResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      if (state.conversationThreadReport?.conversationThreadReportRequestBody
        && state.conversationThreadReport?.conversationThreadReportResultData?.pages?.next) {
        const currentResultSet = state.conversationThreadReport
          .conversationThreadReportResultData;
        const currentJumpToPage = state.conversationThreadReport.jumpToPage || 0;
        // Convert UI page indexes into the API's 1000-record page groups.
        const pageGroupSize = 1000 / state.conversationThreadReport.rowsPerPage;
        const pageGroup = currentJumpToPage > 0
          ? Math.floor(currentJumpToPage / pageGroupSize) : 0;
        const { triggerJumpToPage } = state.conversationThreadReport;
        if (triggerJumpToPage) {
          dispatch(actions.updateTriggerJumpToPage(false));
        }
        let updatedRequest = state.conversationThreadReport
          .conversationThreadReportRequestBody;
        const searchResultNextOffset = state.conversationThreadReport
          .conversationThreadReportResultData.pages.next;
        // Cursor values arrive as query fragments; extract only the offset segment.
        const resNext = searchResultNextOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: triggerJumpToPage
            ? (pageGroup * 1000).toString() : searchResultNextOffset.slice(7, resNext)
        };
        dispatch(togglePaginationSpinner({ isFetchingNextData: true }));
        request({
          api: 'conversationThreadReportSearch',
          method: 'post',
          data: updatedRequest
        }, dispatch, getState)
          .then((response) => {
            dispatch(
              actions.updateConversationThreadReportRequestBodyOnSameBookmark(updatedRequest)
            );
            dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
            dispatch(toggleSpinner(false));
            dispatch(
              actions.updateConversationThreadReportNextResult({ response, currentResultSet })
            );
            if (triggerJumpToPage) {
              dispatch(actions.ChangeReportAppendJumpToPageNextResultSet(
                state.conversationThreadReport.rowsPerPage
              ));
              dispatch(actions.updatePreviousResultSettoNull());
            }
          })
          .catch((error) => {
            dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
            console.log('NEXT Error ', error);
          });
      }
    }
  );
}

/**
 * Creates a thunk wrapper that dispatches the supplied next-page report thunk.
 */
function ChangeReportsDataNextSet() {
  return createAsyncThunk(
    `${name}/ChangeReportsDataNextSet`,
    async ({
      pofetchConversationThreadReportNextResultSet
    }, { dispatch }) => {
      dispatch(pofetchConversationThreadReportNextResultSet());
    }
  );
}

/**
 * Creates a thunk that calls the conversationThreadReportSearch API for the conversation thread
 * report workflow and updates Redux state.
 */
function fetchReportJumpToPagePrevResultSet() {
  return createAsyncThunk(
    `${name}/fetchReportJumpToPagePrevResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      dispatch(actions.updateApiCallBeforePrepend(false));
      if (state.conversationThreadReport?.conversationThreadReportResultData?.pages?.prev) {
        const currentResultSet = state.conversationThreadReport
          .conversationThreadReportResultData;
        let updatedRequest = state.conversationThreadReport
          .conversationThreadReportRequestBody;
        const searchResultPrevOffset = state.conversationThreadReport
          .conversationThreadReportResultData.pages.prev;
        // Cursor values arrive as query fragments; extract only the offset segment.
        const res = searchResultPrevOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: searchResultPrevOffset.slice(7, res)
        };
        dispatch(togglePaginationSpinner({ isFetchingPrevData: true }));
        request({
          api: 'conversationThreadReportSearch',
          method: 'post',
          data: updatedRequest
        }, dispatch, getState)
          .then((response) => {
            dispatch(
              actions.updateConversationThreadReportRequestBodyOnSameBookmark(updatedRequest)
            );
            dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
            dispatch(toggleSpinner(false));
            dispatch(
              actions.updateConversationThreadReportPrevResult({ response, currentResultSet })
            );
          })
          .catch((error) => {
            dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
            console.log('PREV Error ', error);
          });
      }
    }
  );
}

/**
 * Creates a thunk that calls the conversationThreadReportSearch API for the conversation thread
 * report workflow and updates Redux state.
 */
function fetchReportPrevResultSet() {
  return createAsyncThunk(
    `${name}/fetchReportPrevResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      if (state.conversationThreadReport?.conversationThreadReportResultData?.pages?.prev) {
        const currentResultSet = state.conversationThreadReport
          .conversationThreadReportResultData;
        const currentJumpToPage = state.conversationThreadReport.jumpToPage || 0;
        // Convert UI page indexes into the API's 1000-record page groups.
        const pageGroupSize = 1000 / state.conversationThreadReport.rowsPerPage;
        const pageGroup = currentJumpToPage > 0
          ? Math.floor(currentJumpToPage / pageGroupSize) : 0;
        const { triggerJumpToPage } = state.conversationThreadReport;
        if (triggerJumpToPage) {
          dispatch(actions.updateTriggerJumpToPage(false));
        }
        let updatedRequest = state.conversationThreadReport
          .conversationThreadReportRequestBody;
        const searchResultPrevOffset = state.conversationThreadReport
          .conversationThreadReportResultData.pages.prev;
        // Cursor values arrive as query fragments; extract only the offset segment.
        const res = searchResultPrevOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: triggerJumpToPage
            ? (pageGroup * 1000).toString() : searchResultPrevOffset.slice(7, res)
        };
        dispatch(togglePaginationSpinner({ isFetchingPrevData: true }));
        request({
          api: 'conversationThreadReportSearch',
          method: 'post',
          data: updatedRequest
        }, dispatch, getState)
          .then((response) => {
            dispatch(
              actions.updateConversationThreadReportRequestBodyOnSameBookmark(updatedRequest)
            );
            dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
            dispatch(toggleSpinner(false));
            dispatch(
              actions.updateConversationThreadReportPrevResult({ response, currentResultSet })
            );
            if (triggerJumpToPage) {
              dispatch(actions.ChangeReportAppendJumpToPagePrevResultSet(
                state.conversationThreadReport.rowsPerPage
              ));
              dispatch(actions.updateNextResultSettoNull());
            }
          })
          .catch((error) => {
            dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
            console.log('PREV Error ', error);
          });
      }
    }
  );
}

/**
 * Creates a thunk wrapper that dispatches the supplied previous-page report thunk.
 */
function ChangeReportsDataPrevSet() {
  return createAsyncThunk(
    `${name}/ChangeReportsDataPrevSet`,
    async ({
      pofetchReportPrevResultSet
    }, { dispatch }) => {
      dispatch(pofetchReportPrevResultSet());
    }
  );
}

/**
 * Creates a thunk that calls the getConversationCodes API for the conversation thread report
 * workflow and updates Redux state.
 */
function getConversationCodes() {
  return createAsyncThunk(
    `${name}/getConversationCodes`,
    async ({
      callback
    }, { getState, dispatch }) => {
      const payload = {};
      dispatch(toggleSpinner(true));
      dispatch(updateDialogStates({ getConversationCodesSpinner: true }));
      const fields = 'ConversationCode,ConversationDescription,modifiedBy,modifiedDate,isActive,id,conversationCategory';
      const filters = 'isActive(false),isActive(true)';
      const query = `?fields=${fields}&filter=${filters}`;
      request({
        api: 'getConversationCodes',
        routeParams: { query },
        method: 'get'
      }, dispatch, getState).then((response) => {
        dispatch(toggleSpinner(false));
        dispatch(updateDialogStates({ getConversationCodesSpinner: false }));
        payload.conversationCodes = response.data;
        dispatch(actions.getMetaData(payload));
        if (callback) {
          callback(response);
        }
      }).catch((error) => {
        dispatch(toggleSpinner(false));
        dispatch(updateDialogStates({ getConversationCodesSpinner: false }));
        if (callback) {
          callback(null, error);
        }
      });
    }
  );
}

/**
 * Creates async thunk actions exposed with the slice actions.
 */
function createExtraActions() {
  return {
    fetchConversationThreadReports: fetchConversationThreadReports(),
    exportConversationThreadReport: exportConversationThreadReport(),
    fetchSavedConversationThreadReportResults: fetchSavedConversationThreadReportResults(),
    fetchConversationThreadreportsResultsOffset: fetchConversationThreadreportsResultsOffset(),
    conversationThreadReportDataOffset: conversationThreadReportDataOffset(),
    fetchConversationThreadReportNextResultSet: fetchConversationThreadReportNextResultSet(),
    ChangeReportsDataNextSet: ChangeReportsDataNextSet(),
    fetchReportPrevResultSet: fetchReportPrevResultSet(),
    ChangeReportsDataPrevSet: ChangeReportsDataPrevSet(),
    getConversationCodes: getConversationCodes(),
    fetchReportJumpToPagePrevResultSet: fetchReportJumpToPagePrevResultSet(),
    fetchConversationThreadReportJumpToPageNextResultSet:
      fetchConversationThreadReportJumpToPageNextResultSet()
  };
}

export const extraActions = createExtraActions();

export const conversationThreadReportActions = { ...actions, ...extraActions };
export const conversationThreadReportReducer = slice.reducer;
